/**
 * Property DTO ↔ View mapper.
 *
 * One direction (BE → FE) for now. When the create wizard lands
 * (SCRUM-178) the reverse mapper (CreatePropertyDto ← form state)
 * sits next to it.
 *
 * Why a dedicated mapper module:
 *
 *   1. **Bundle alignment.** The bundle's `DATA_MODEL.md` uses
 *      different field names than the BE DTO (see SCRUM-167 plan
 *      "BE shape vs. bundle field aliasing" table). The mapper is
 *      the single seam where the alignment happens — pages consume
 *      `PropertyView` and never see `PropertyResponseDto`.
 *
 *   2. **Status-enum normalisation.** BE uses `"non_bookable"`
 *      (underscore); the bundle + every FE primitive uses
 *      `"non-bookable"` (hyphen). The mapper normalises so downstream
 *      code can match the bundle's vocabulary exactly.
 *
 *   3. **Future BE evolution.** When the BE adds `spaces[]` etc, the
 *      mapper grows. Pages don't.
 *
 * Adversarial coverage in `__tests__/dto-mapper.spec.ts` —
 * round-trip + each enum branch + missing optional fields.
 */

import type { components } from '@artemis/contracts';
import type {
  PropertyType,
  PropertyView,
  UnitAvailability,
  UnitOperational,
} from './types.js';

type PropertyResponseDto = components['schemas']['PropertyResponseDto'];

/**
 * The closed set of BE `unitCategory` values the mapper knows about
 * today, declared as a tuple so its `Lookup` keys give exhaustive
 * narrowing on `mapUnitCategoryToType`. When the BE adds a new
 * enum member (e.g. `'studio'`) the const tuple grows + the lookup
 * gets the new key; TypeScript fails the diff at typecheck time
 * via the `KnownCategory` union assignment in the mapper.
 *
 * This restores the exhaustiveness guarantee dropped earlier when
 * the inline `switch` couldn't narrow `category` to `never` due to
 * an openapi-generated type widening issue. The lookup-table form
 * works because the keys are statically known.
 */
const UNIT_CATEGORY_TO_TYPE = {
  villa: 'Villa',
  home: 'Home',
  condominium: 'Condominium',
  cabin: 'Cabin',
  apartment: 'Apartment',
  townhome: 'Townhome',
  other: 'Other',
} as const satisfies Readonly<
  Record<PropertyResponseDto['unitCategory'], PropertyType>
>;

type KnownCategory = keyof typeof UNIT_CATEGORY_TO_TYPE;

/**
 * The `satisfies` clause above forces TypeScript to verify the
 * lookup table covers every member of `PropertyResponseDto['unitCategory']`
 * (currently 7). If a future BE regen adds an enum member, the
 * `satisfies` clause fails compilation here, prompting the
 * contributor to add the mapping. The runtime fallback to `'Other'`
 * handles the edge case where the FE has been deployed with an
 * older type contract than the BE response.
 */
function mapUnitCategoryToType(
  category: PropertyResponseDto['unitCategory'],
): PropertyType {
  // The keyof check is a structural narrowing — if `category` is a
  // known key, return the mapped value; otherwise fall back to
  // 'Other' so a future BE enum extension doesn't crash the UI
  // before the typecheck catches it in CI.
  return Object.hasOwn(UNIT_CATEGORY_TO_TYPE, category)
    ? UNIT_CATEGORY_TO_TYPE[category as KnownCategory]
    : 'Other';
}

/** Bundle uses hyphen; BE uses underscore. Mapper translates. */
function mapVisibility(
  visibility: PropertyResponseDto['status']['visibility'],
): UnitAvailability {
  return visibility === 'non_bookable' ? 'non-bookable' : 'bookable';
}

/** Identity — both sides use 'active' / 'inactive'. Kept as a named function so the contract is visible. */
function mapBilling(
  billing: PropertyResponseDto['status']['billing'],
): UnitOperational {
  return billing;
}

/**
 * Project a single `PropertyResponseDto` to the FE's `PropertyView`.
 * Pure — no side effects, no fetches, no defaults that aren't
 * explicit. Optional shell fields stay `undefined` (see
 * `types.ts` — never coerce to mock data).
 */
export function projectPropertyDto(
  dto: PropertyResponseDto,
): PropertyView {
  const view: PropertyView = {
    id: dto.id,
    name: dto.unitName,
    type: mapUnitCategoryToType(dto.unitCategory),
    address: {
      // `PropertyAddressDto` shape from openapi.d.ts: { line1, line2?,
      // city, stateOrProvince, postalCode, countryCode, timezone }.
      // The mapper passes the BE names through verbatim — see
      // `types.ts` for the rationale (keeping BE field names makes
      // round-tripping cleaner when the wizard's POST mapper lands).
      line1: dto.address.line1,
      city: dto.address.city,
      stateOrProvince: dto.address.stateOrProvince,
      postalCode: dto.address.postalCode,
      countryCode: dto.address.countryCode,
      timezone: dto.address.timezone,
      ...(dto.address.line2 !== null && dto.address.line2 !== undefined
        ? { line2: dto.address.line2 }
        : {}),
    },
    capacity: {
      maxSleeps: dto.capacity.maxSleeps,
      maxAdults: dto.capacity.maxAdults,
      bedrooms: dto.capacity.bedrooms,
      bathroomsFull: dto.capacity.bathroomsFull,
      bathroomsHalf: dto.capacity.bathroomsHalf,
      bathroomsShower: dto.capacity.bathroomsShower,
      bathroomsBathtub: dto.capacity.bathroomsBathtub,
      allowsChildren: dto.capacity.allowsChildren,
      allowsPets: dto.capacity.allowsPets,
      ...(dto.capacity.checkInOverride !== null &&
      dto.capacity.checkInOverride !== undefined
        ? { checkInOverride: dto.capacity.checkInOverride }
        : {}),
      ...(dto.capacity.checkOutOverride !== null &&
      dto.capacity.checkOutOverride !== undefined
        ? { checkOutOverride: dto.capacity.checkOutOverride }
        : {}),
    },
    photoCount: dto.photoCount,
    status: {
      op: mapBilling(dto.status.billing),
      av: mapVisibility(dto.status.visibility),
    },
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    version: dto.version,
    ...(dto.websiteNameOverride !== null &&
    dto.websiteNameOverride !== undefined
      ? { nickname: dto.websiteNameOverride }
      : {}),
    ...(dto.description !== null && dto.description !== undefined
      ? { description: dto.description }
      : {}),
    ...(dto.unitAlias !== null && dto.unitAlias !== undefined
      ? { alias: dto.unitAlias }
      : {}),
    ...(dto.archivedAt !== null && dto.archivedAt !== undefined
      ? { archivedAt: dto.archivedAt }
      : {}),
    ...(dto.gpsCoordinates !== null && dto.gpsCoordinates !== undefined
      ? {
          gps: {
            latitude: dto.gpsCoordinates.latitude,
            longitude: dto.gpsCoordinates.longitude,
          },
        }
      : {}),
  };
  return view;
}
